import {
  getFeesForNewLicenseOptions,
  getFeesForRenewalOptions,
  getLicenseTypeConfigurationOptions,
  getLicenseTypeQuestionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useQuery } from "@tanstack/react-query";
import { format, parse } from "date-fns";

interface UseLicenseFormDataOptions {
  licenseTypeId?: string;
  jurisdictionId?: string;
  licenseId?: string;
  effectiveDate?: string;
  isRenewal?: boolean;
  includeFees?: boolean;
  propertyStructureAnswer?: string;
  commercialManagedPropertyAnswer?: boolean;
  numberOfUnitsAnswer?: number;
  vacancyAnswer?: boolean;
  inspectionExemptionAnswer?: string; // Date string in YYYY-MM-DD format
  omtPartyId?: string | null;
}

export function useLicenseFormData({
  licenseTypeId,
  jurisdictionId,
  licenseId,
  effectiveDate,
  isRenewal = false,
  includeFees = true,
  propertyStructureAnswer,
  commercialManagedPropertyAnswer,
  numberOfUnitsAnswer,
  vacancyAnswer,
  inspectionExemptionAnswer,
  omtPartyId,
}: UseLicenseFormDataOptions) {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  const renewalFeeQueryParams = {
    ...(propertyStructureAnswer
      ? { property_structure_answer: propertyStructureAnswer }
      : {}),
    ...(commercialManagedPropertyAnswer !== undefined
      ? {
          commercial_managed_property_answer: commercialManagedPropertyAnswer,
        }
      : {}),
    ...(numberOfUnitsAnswer !== undefined
      ? { number_of_units_answer: numberOfUnitsAnswer }
      : {}),
    ...(vacancyAnswer !== undefined ? { vacancy_answer: vacancyAnswer } : {}),
    ...(omtPartyId ? { omt_party_id: omtPartyId } : {}),
  };

  // Get fees data for renewal
  const renewalFeesQuery = useQuery({
    ...getFeesForRenewalOptions({
      path: { license_id: licenseId || "" },
      query: renewalFeeQueryParams,
    }),
    enabled: isAuthSetupComplete && isRenewal && includeFees && !!licenseId,
  });

  // Get fees data for new license
  const newLicenseFeesQuery = useQuery({
    ...getFeesForNewLicenseOptions({
      path: {
        license_type_id: licenseTypeId || "",
        jurisdiction_id: jurisdictionId || "",
      },
      query: {
        effective_date: effectiveDate
          ? format(parse(effectiveDate, "yyyy-MM-dd", new Date()), "yyyy-MM-dd")
          : "",
        ...(propertyStructureAnswer
          ? { property_structure_answer: propertyStructureAnswer }
          : {}),
        ...(commercialManagedPropertyAnswer !== undefined
          ? {
              commercial_managed_property_answer:
                commercialManagedPropertyAnswer,
            }
          : {}),
        ...(numberOfUnitsAnswer !== undefined
          ? { number_of_units_answer: numberOfUnitsAnswer }
          : {}),
        ...(vacancyAnswer !== undefined
          ? { vacancy_answer: vacancyAnswer }
          : {}),
        ...(inspectionExemptionAnswer
          ? { inspection_exemption_answer: inspectionExemptionAnswer }
          : {}),
        ...(omtPartyId ? { omt_party_id: omtPartyId } : {}),
      },
    }),
    enabled:
      isAuthSetupComplete &&
      !isRenewal &&
      includeFees &&
      !!licenseTypeId &&
      !!jurisdictionId &&
      !!effectiveDate,
  });

  // Get configuration data
  const configurationQuery = useQuery({
    ...getLicenseTypeConfigurationOptions({
      path: {
        license_type_id: licenseTypeId || "",
        jurisdiction_id: jurisdictionId || "",
      },
    }),
    enabled: isAuthSetupComplete && !!licenseTypeId && !!jurisdictionId,
  });

  // Get questions data
  const questionsQuery = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseTypeId || "",
        jurisdiction_id: jurisdictionId || "",
      },
    }),
    enabled: isAuthSetupComplete && !!licenseTypeId && !!jurisdictionId,
  });

  // Combine results based on renewal status
  const feesData = isRenewal ? renewalFeesQuery.data : newLicenseFeesQuery.data;
  const areFeesLoading = isRenewal
    ? renewalFeesQuery.isLoading
    : newLicenseFeesQuery.isLoading;
  const feesQueryError = isRenewal
    ? renewalFeesQuery.error
    : newLicenseFeesQuery.error;

  const isLoading =
    areFeesLoading || configurationQuery.isLoading || questionsQuery.isLoading;
  const error =
    feesQueryError || configurationQuery.error || questionsQuery.error;

  return {
    feesData,
    configurationData: configurationQuery.data,
    questionsData: questionsQuery.data,
    isLoading,
    error,
  };
}
